import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { useGestor } from "@/pages/_payments/CarteraCliente";
import { descargarArchivo } from "@/components/NotaDescarga";
import { SalesCNModal } from "@/pages/_credit/SalesCreditNotes";
import {
  ClockIcon, HourglassMediumIcon, CheckCircleIcon, ProhibitIcon, XIcon, CheckIcon, FileTextIcon, PaperclipIcon, WarningIcon, ArrowLeftIcon,
} from "@phosphor-icons/react";

const fh = (iso) => (iso ? new Date(iso).toLocaleString("es-HN", { dateStyle: "short", timeStyle: "short" }) : "");
const ESTADO = {
  pendiente_aprobacion: { l: "Pendiente de aprobación", i: ClockIcon, c: "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10" },
  aprobado_pendiente_generar: { l: "Aprobado, pendiente de generar", i: HourglassMediumIcon, c: "text-blue-300 border-blue-500/40 bg-blue-500/10" },
  generando: { l: "Generando…", i: HourglassMediumIcon, c: "text-blue-300 border-blue-500/40 bg-blue-500/10" },
  nc_generada: { l: "Nota de crédito generada", i: CheckCircleIcon, c: "text-agro-green border-agro-green/40 bg-agro-green/10" },
  rechazado: { l: "Rechazado", i: ProhibitIcon, c: "text-red-300 border-red-500/40 bg-red-500/10" },
};
function Estado({ e }) {
  const s = ESTADO[e] || ESTADO.pendiente_aprobacion;
  const I = s.i;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${s.c}`}><I size={11} /> {s.l}</span>;
}
const cuenta = (c) => (c ? `${c.codigo ? `${c.codigo} · ` : ""}${c.nombre || "Cuenta principal"}` : "—");

/** Traslados a subcuenta NC: Pendientes de aprobación · Pendientes de generar · Historial (generadas y rechazadas). */
export default function TrasladosNc({ bandeja }) {
  const { admin } = useGestor();
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const cargar = useCallback(() => {
    setItems(null);
    api.get("/cobranza/nc-traslados", { params: { bandeja } }).then(({ data }) => setItems(data)).catch((e) => { toast.error(apiErr(e)); setItems([]); });
  }, [bandeja]);
  useEffect(() => { cargar(); setSel(null); }, [cargar]);
  if (sel) return <Detalle id={sel} admin={admin} onBack={() => { setSel(null); cargar(); }} />;
  if (!items) return <div className="text-sm text-text-muted">Cargando…</div>;
  if (!items.length) return <div className="bg-card border border-border/60 rounded-xl p-8 text-center text-sm text-text-secondary" data-testid="nc-bandeja-vacia">Sin solicitudes en esta bandeja.</div>;
  return (
    <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid={`nc-bandeja-${bandeja}`}>
      {items.map((t) => (
        <li key={t.id} className="bg-card border border-border/60 rounded-xl p-4 text-sm" data-testid={`nc-tr-${t.id}`}>
          <div className="flex flex-wrap justify-between gap-2">
            <div className="min-w-0"><div className="font-semibold truncate">{t.cliente_nombre}</div>
              <div className="text-xs text-text-muted"><span className="font-mono">{t.factura_numero}</span> · {cuenta(t.origen)}</div></div>
            <div className="text-right"><div className="font-semibold text-agro-yellow tabular-nums">{fmtL(t.monto)}</div><Estado e={t.estado} /></div>
          </div>
          <div className="mt-2 text-xs line-clamp-2"><span className="text-text-muted">Motivo:</span> {t.motivo}</div>
          <div className="text-[11px] text-text-muted mt-0.5">{t.solicitante_nombre} · {fh(t.fecha)}{t.nc_numero ? ` · NC ${t.nc_numero}` : ""}</div>
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={() => setSel(t.id)} data-testid={`nc-ver-${t.id}`}
              className="min-h-[44px] px-4 border border-border rounded-md text-xs font-semibold flex items-center gap-1.5"><FileTextIcon size={14} /> Ver detalle</button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Detalle({ id, admin, onBack }) {
  const [t, setT] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [tipo, setTipo] = useState("ajuste");
  const [isv, setIsv] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [formulario, setFormulario] = useState(false);
  const cargar = useCallback(() => api.get(`/cobranza/nc-traslados/${id}`).then(({ data }) => setT(data)).catch((e) => toast.error(apiErr(e))), [id]);
  useEffect(() => { cargar(); }, [cargar]);
  const accion = async (url, body, ok) => {
    if (enviando) return;
    setEnviando(true);
    try { const { data } = await api.post(url, body); setT({ ...t, ...data }); toast.success(ok); cargar(); }
    catch (e) { toast.error(apiErr(e)); } finally { setEnviando(false); }
  };
  if (!t) return <div className="text-sm text-text-muted">Cargando…</div>;
  const f = t.factura || {};
  return (
    <div className="space-y-4 max-w-3xl" data-testid="nc-detalle">
      {formulario && <SalesCNModal traslado={t} onClose={() => setFormulario(false)} onSaved={() => { setFormulario(false); toast.success("Nota de crédito emitida"); cargar(); }} />}
      <button type="button" onClick={onBack} className="min-h-[44px] text-sm text-text-secondary flex items-center gap-1.5"><ArrowLeftIcon size={16} /> Volver a la bandeja</button>
      <section className="bg-card border border-border/60 rounded-xl p-4 text-sm">
        <div className="flex flex-wrap justify-between gap-2 mb-3">
          <div><div className="font-heading text-base font-semibold">{t.cliente_nombre}</div><div className="text-xs text-text-muted font-mono">{t.factura_numero}</div></div>
          <Estado e={t.estado} />
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <dt className="text-text-muted">Cuenta de origen</dt><dd>{cuenta(t.origen)}</dd>
          <dt className="text-text-muted">Destino</dt><dd>Subcuenta NC del cliente</dd>
          <dt className="text-text-muted">Importe</dt><dd className="font-semibold text-agro-yellow">{fmtL(t.monto)}</dd>
          <dt className="text-text-muted">Saldo actual de la factura</dt><dd>{fmtL(f.saldo_pendiente)}{f.saldo_nc ? ` · en NC ${fmtL(f.saldo_nc)}` : ""}</dd>
          <dt className="text-text-muted">Emisión / vencimiento</dt><dd>{fmtDate(f.fecha)} · {fmtDate(f.fecha_vencimiento)}</dd>
          <dt className="text-text-muted">Solicitante</dt><dd>{t.solicitante_nombre} · {fh(t.fecha)}</dd>
          <dt className="text-text-muted col-span-2">Motivo</dt><dd className="col-span-2 whitespace-pre-wrap">{t.motivo}</dd>
          {t.motivo_rechazo && <><dt className="text-text-muted col-span-2">Motivo del rechazo</dt><dd className="col-span-2 text-red-300">{t.motivo_rechazo}</dd></>}
          {t.nc_numero && <><dt className="text-text-muted">Nota de crédito</dt><dd className="font-mono">{t.nc_numero} · {fmtL(t.nc_total)}</dd></>}
        </dl>
        {t.adjunto && (
          <button type="button" onClick={() => descargarArchivo(`/cobranza/nc-traslados/adjuntos/${t.adjunto.id}`, t.adjunto.nombre).catch((e) => toast.error(apiErr(e)))}
            className="mt-3 min-h-[44px] px-3 border border-border rounded-md text-xs flex items-center gap-1.5"><PaperclipIcon size={14} /> {t.adjunto.nombre}</button>
        )}
        {f.saldo_cambio && <p className="mt-3 text-xs text-agro-yellow flex items-center gap-1.5"><WarningIcon size={14} /> El saldo de la factura cambió desde la solicitud: revisa el importe antes de aprobar.</p>}
      </section>

      {admin && t.estado === "pendiente_aprobacion" && (
        <section className="bg-card border border-border/60 rounded-xl p-4 space-y-2">
          <h4 className="text-sm font-semibold">Revisión del Administrador</h4>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (obligatorio para rechazar)" data-testid="nc-motivo-resolver"
            className="w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm" />
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" disabled={enviando || motivo.trim().length < 3} onClick={() => accion(`/cobranza/nc-traslados/${id}/resolver`, { accion: "rechazar", motivo }, "Solicitud rechazada")}
              data-testid="nc-rechazar" className="min-h-[44px] px-4 border border-red-500/50 text-red-300 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40"><XIcon size={14} /> Rechazar</button>
            <button type="button" disabled={enviando} onClick={() => accion(`/cobranza/nc-traslados/${id}/resolver`, { accion: "aprobar", motivo }, "Traslado aprobado: pendiente de generar")}
              data-testid="nc-aprobar" className="min-h-[44px] px-4 bg-agro-green text-black rounded-md text-xs font-semibold flex items-center gap-1.5"><CheckIcon size={14} weight="bold" /> Aprobar</button>
          </div>
        </section>
      )}

      {admin && t.estado === "aprobado_pendiente_generar" && (
        <section className="bg-card border border-border/60 rounded-xl p-4 space-y-3">
          <h4 className="text-sm font-semibold">Generar la nota de crédito</h4>
          <p className="text-xs text-text-secondary">Se emite con las reglas actuales del sistema (número fiscal propio de la nota, reducción del saldo de la factura y asiento) hasta <b>{fmtL(t.monto)}</b>, vinculada a la factura y a esta solicitud.</p>
          <button type="button" onClick={() => setFormulario(true)} data-testid="nc-formulario"
            className="w-full min-h-[44px] px-4 border border-agro-green/50 text-agro-green rounded-md text-sm font-semibold flex items-center justify-center gap-1.5">
            <FileTextIcon size={16} /> Abrir el formulario de nota de crédito (devolución de productos u otro tipo, con resumen y vista previa)</button>
          <div className="text-[11px] text-text-muted">O emite directamente un ajuste de valor por el importe completo:</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-text-secondary">Tipo de nota
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="mt-1 w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm" data-testid="nc-tipo">
                <option value="ajuste">Ajuste</option><option value="descuento">Descuento</option><option value="bono">Bonificación</option>
              </select></label>
            <label className="text-xs text-text-secondary">ISV incluido en el importe
              <select value={isv} onChange={(e) => setIsv(Number(e.target.value))} className="mt-1 w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm" data-testid="nc-isv">
                <option value={0}>Exento (sin ISV)</option><option value={15}>Incluye ISV 15 %</option><option value={18}>Incluye ISV 18 %</option>
              </select></label>
          </div>
          <div className="flex justify-end">
            <button type="button" disabled={enviando} onClick={() => window.confirm(`¿Generar la nota de crédito por ${fmtL(t.monto)} para la factura ${t.factura_numero}?`)
              && accion(`/cobranza/nc-traslados/${id}/generar`, { tipo, isv_pct: isv }, "Nota de crédito generada")}
              data-testid="nc-generar" className="min-h-[44px] px-4 bg-agro-green text-black rounded-md text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
              <FileTextIcon size={16} weight="bold" /> {enviando ? "Generando…" : "Generar nota de crédito"}</button>
          </div>
        </section>
      )}

      <section className="bg-card border border-border/60 rounded-xl p-4">
        <h4 className="text-sm font-semibold mb-2">Historial</h4>
        <ol className="space-y-2">
          {(t.historial || []).map((h, i) => (
            <li key={i} className="border-l-2 border-border pl-3 text-xs">
              <div><b>{({ solicitado: "Solicitado · fuera del estado de cuenta ordinario", aprobado: "Aprobado", rechazado: "Rechazado · regresa a su cuenta de origen", nc_generada: "Nota de crédito generada" })[h.accion] || h.accion}</b>
                <span className="text-text-muted"> · {h.usuario_nombre} · {fh(h.fecha)}</span></div>
              {h.detalle && <div className="text-text-secondary">{h.detalle}</div>}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
